import React, { useEffect, useState, useMemo } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useMediaQuery,
    TextField,
    Box,
    Checkbox,
    MenuItem,
    ListItemText,
    Select,
    OutlinedInput,
    Typography
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import axios from '../axios';
import Spinner from './Spinner';
import { selectUser } from 'features/userSlice';

export default function PackageSetModel({
    products,
    prodError,
    farmerId,
    selectedPackage,
    isEditMode = false
}) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState('');
    const theme = useTheme();
    const user = useSelector(selectUser);
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    // Derive selected product names using useMemo
    const selectedProductNames = useMemo(
        () => selectedProducts.map((p) => p.product || p.name),
        [selectedProducts]
    );

    useEffect(() => {
        if (isEditMode && selectedPackage) {
            setName(selectedPackage.name);
            setSelectedProducts(selectedPackage.products);
            setTotal(selectedPackage.totalAmount);
        }
    }, [isEditMode, selectedPackage]);

    const handleClickOpen = () => {
        if (prodError) {
            alert("Couldn't fetch products, please refresh.");
        } else if (!products.length) {
            alert('Please wait as products are loading.');
        } else {
            setOpen(true);
        }
    };

    const handleCountModification = (action, index) => {
        setSelectedProducts((prevSelectedProducts) =>
            prevSelectedProducts.map((product, idx) =>
                idx === index
                    ? {
                          ...product,
                          count:
                              action === 'add' ? product.count + 1 : Math.max(1, product.count - 1)
                      }
                    : product
            )
        );
    };

    const handleChange = (event) => {
        const valueArray =
            typeof event.target.value === 'string'
                ? event.target.value.split(',')
                : event.target.value;

        const updatedProducts = valueArray.map((productName) => ({
            product: productName,
            count: 1
        }));

        setSelectedProducts(updatedProducts);
    };

    useEffect(() => {
        const totalAmount = selectedProducts.reduce((sum, selected) => {
            const product = products.find(
                (p) => p.name === selected.product || p.name === selected.name
            );
            return product ? sum + parseFloat(product.unitPrice) * parseFloat(selected.count) : sum;
        }, 0);

        setTotal(totalAmount);
    }, [selectedProducts, products]);

    const handleClose = () => setOpen(false);

    const savePackage = async () => {
        setLoading(true);
        try {
            const productList = selectedProducts.map((selected) => {
                const product = products.find(
                    (p) => p.name === selected.product || p.name === selected.name
                );
                return {
                    productID: product?._id,
                    name: product.name,
                    price: product.unitPrice,
                    count: selected.count
                };
            });

            const packageData = {
                name,
                adderID: user.id,
                products: productList,
                totalAmount: total
            };
            if (!isEditMode) {
                packageData.owner = farmerId;
            }

            const request = isEditMode
                ? axios.patch(`/packages/${selectedPackage._id}`, packageData)
                : axios.post('/packages/add-package', packageData);

            await request;
            window.location.href = '/dashboard';
        } catch (error) {
            setError('Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Button
                variant='contained'
                onClick={handleClickOpen}
                sx={{
                    backgroundColor: '#085d40',
                    color: '#ffffff',
                    borderRadius: '10px',
                    padding: '10px 10px',
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 2px 8px rgba(8, 93, 64, 0.25)',
                    '&:hover': {
                        backgroundColor: '#064532',
                        boxShadow: '0 4px 12px rgba(8, 93, 64, 0.35)'
                    }
                }}
            >
                {isEditMode ? 'Edit Package' : 'Add Package'}
            </Button>
            <Dialog
                fullScreen={fullScreen}
                open={open}
                onClose={handleClose}
                aria-labelledby='responsive-dialog-title'
                maxWidth='md'
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        border: '1px solid #e5e7eb'
                    }
                }}
            >
                <DialogTitle
                    id='responsive-dialog-title'
                    sx={{
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid #e5e7eb',
                        padding: '24px',
                        fontSize: '24px',
                        fontWeight: 700,
                        color: '#1f2937'
                    }}
                >
                    {isEditMode ? 'Edit Package' : 'Add New Package'}
                </DialogTitle>
                <DialogContent sx={{ padding: '32px', backgroundColor: '#ffffff' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {/* Package Name Section */}
                        <Box>
                            <TextField
                                required
                                fullWidth
                                id='package-name'
                                onChange={(e) => setName(e.target.value)}
                                value={name}
                                label='Package Name'
                                variant='outlined'
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: '#f9fafb',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#085d40'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#085d40',
                                            borderWidth: '2px'
                                        }
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#085d40'
                                    }
                                }}
                            />
                        </Box>

                        {/* Products Selection Section */}
                        <Box
                            sx={{
                                backgroundColor: '#f8fafc',
                                borderRadius: '16px',
                                padding: '24px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            <Box sx={{ marginBottom: '16px' }}>
                                <Typography
                                    variant='h6'
                                    sx={{
                                        color: '#1f2937',
                                        fontWeight: 600,
                                        marginBottom: '8px'
                                    }}
                                >
                                    Select Products
                                </Typography>
                                <Typography variant='body2' sx={{ color: '#6b7280' }}>
                                    Choose the products to include in this package
                                </Typography>
                            </Box>

                            <Select
                                multiple
                                fullWidth
                                value={selectedProductNames}
                                onChange={handleChange}
                                input={<OutlinedInput />}
                                renderValue={(selected) => selected.join(', ')}
                                sx={{
                                    borderRadius: '12px',
                                    backgroundColor: '#ffffff',
                                    marginBottom: '20px',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#d1d5db'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#085d40'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#085d40'
                                    }
                                }}
                            >
                                {products.map((prod) => (
                                    <MenuItem
                                        key={prod._id}
                                        value={prod.name}
                                        sx={{
                                            padding: '12px 16px',
                                            '&:hover': {
                                                backgroundColor: '#f0fdf4'
                                            }
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedProductNames.includes(prod.name)}
                                            sx={{
                                                color: '#085d40',
                                                '&.Mui-checked': {
                                                    color: '#085d40'
                                                }
                                            }}
                                        />
                                        <ListItemText
                                            primary={prod.name}
                                            secondary={`UGX ${prod.unitPrice}`}
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontWeight: 500,
                                                    color: '#1f2937'
                                                },
                                                '& .MuiListItemText-secondary': {
                                                    color: '#085d40',
                                                    fontWeight: 600
                                                }
                                            }}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>

                            {/* Total Display */}
                            <Box
                                sx={{
                                    backgroundColor: '#ecfdf5',
                                    border: '2px solid #10b981',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    textAlign: 'center',
                                    marginBottom: '20px'
                                }}
                            >
                                <Typography
                                    variant='h5'
                                    sx={{
                                        color: '#065f46',
                                        fontWeight: 700,
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    Total: UGX {total.toLocaleString()}
                                </Typography>
                            </Box>

                            {/* Selected Products with Quantity Controls */}
                            {selectedProducts.length > 0 && (
                                <Box>
                                    <Typography
                                        variant='subtitle1'
                                        sx={{
                                            color: '#1f2937',
                                            fontWeight: 600,
                                            marginBottom: '12px'
                                        }}
                                    >
                                        Selected Products ({selectedProducts.length})
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '12px'
                                        }}
                                    >
                                        {selectedProducts.map((prod, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: '12px',
                                                    padding: '16px',
                                                    border: '1px solid #e5e7eb',
                                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                                }}
                                            >
                                                <Typography
                                                    variant='body1'
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: '#1f2937',
                                                        flex: 1
                                                    }}
                                                >
                                                    {prod.product || prod.name}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        backgroundColor: '#f9fafb',
                                                        borderRadius: '8px',
                                                        padding: '8px'
                                                    }}
                                                >
                                                    <Button
                                                        size='small'
                                                        onClick={() =>
                                                            handleCountModification('sub', index)
                                                        }
                                                        sx={{
                                                            minWidth: '32px',
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '6px',
                                                            backgroundColor: '#fee2e2',
                                                            color: '#dc2626',
                                                            '&:hover': {
                                                                backgroundColor: '#fecaca'
                                                            }
                                                        }}
                                                    >
                                                        <ArrowBackIos sx={{ fontSize: '16px' }} />
                                                    </Button>
                                                    <Typography
                                                        variant='h6'
                                                        sx={{
                                                            minWidth: '24px',
                                                            textAlign: 'center',
                                                            fontWeight: 600,
                                                            color: '#1f2937'
                                                        }}
                                                    >
                                                        {prod.count}
                                                    </Typography>
                                                    <Button
                                                        size='small'
                                                        onClick={() =>
                                                            handleCountModification('add', index)
                                                        }
                                                        sx={{
                                                            minWidth: '32px',
                                                            width: '32px',
                                                            height: '32px',
                                                            borderRadius: '6px',
                                                            backgroundColor: '#dcfce7',
                                                            color: '#16a34a',
                                                            '&:hover': {
                                                                backgroundColor: '#bbf7d0'
                                                            }
                                                        }}
                                                    >
                                                        <ArrowForwardIos
                                                            sx={{ fontSize: '16px' }}
                                                        />
                                                    </Button>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Error Display */}
                        {error && (
                            <Box
                                sx={{
                                    backgroundColor: '#fef2f2',
                                    border: '1px solid #fecaca',
                                    borderRadius: '12px',
                                    padding: '12px',
                                    color: '#dc2626',
                                    fontWeight: 500
                                }}
                            >
                                {error}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        backgroundColor: '#f8fafc',
                        borderTop: '1px solid #e5e7eb',
                        padding: '24px',
                        gap: '12px'
                    }}
                >
                    <Button
                        autoFocus
                        onClick={handleClose}
                        variant='outlined'
                        sx={{
                            borderColor: '#d1d5db',
                            color: '#6b7280',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f3f4f6'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Spinner />
                            <Typography variant='body2' sx={{ color: '#6b7280' }}>
                                {isEditMode ? 'Updating package...' : 'Creating package...'}
                            </Typography>
                        </Box>
                    ) : (
                        <Button
                            onClick={savePackage}
                            variant='contained'
                            disabled={!name || selectedProducts.length === 0}
                            sx={{
                                backgroundColor: '#085d40',
                                borderRadius: '10px',
                                padding: '10px 20px',
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: '0 2px 8px rgba(8, 93, 64, 0.25)',
                                '&:hover': {
                                    backgroundColor: '#064532',
                                    boxShadow: '0 4px 12px rgba(8, 93, 64, 0.35)'
                                },
                                '&:disabled': {
                                    backgroundColor: '#d1d5db',
                                    color: '#9ca3af'
                                }
                            }}
                        >
                            {isEditMode ? 'Update Package' : 'Create Package'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
}
